import { ref, computed } from 'vue';
import {
  registerBattlepackBundle,
  hasAuthoredContent,
  type BattlePack,
  type BattleplanEntry,
  type BattlepackBundle,
  type IBattleplanMap,
  type IBattleplan,
  type IBattleTacticCard,
  type IRegimentOfRenown,
  type RuleDocument,
  type AuthoredArmy,
  makeAuthoredArmy,
  registerArmyBundle,
  withDerivedUnitKeywords,
  stripDerivedUnitKeywords,
  withDerivedRegimentUnitKeywords,
  stripDerivedRegimentUnitKeywords,
} from '@scrolldex/shared';

// Injected at build time by Vite (see vite.config.ts `define`). Falls back to
// 'dev' when running outside the bundler (e.g. unit tests).
declare const __PKG_VERSION__: string;
const STUDIO_VERSION = typeof __PKG_VERSION__ === 'string' ? __PKG_VERSION__ : 'dev';

type AuthoredFields = Pick<
  IBattleplan,
  'rulesText' | 'twist' | 'scoringText' | 'scoringRules' | 'abilities' | 'sections'
>;

interface Snapshot {
  battleplans: Record<string, BattleplanEntry>;
  currentPlan: string | null;
  rulesDocuments: RuleDocument[];
  battleTactics: IBattleTacticCard[];
  armies: AuthoredArmy[];
  regimentsOfRenown: IRegimentOfRenown[];
}

const HISTORY_LIMIT = 50;

const battlePack = ref<BattlePack | null>(null);
// Unified per-battleplan content keyed by plan name: each entry holds whatever
// content exists (a map, authored rules, or both). Replaces the previously-
// separate maps + authored records.
const battleplans = ref<Record<string, BattleplanEntry>>({});
const currentPlan = ref<string | null>(null);
// Bumped on every syncRegistry() call so computed properties that depend on the
// registry (e.g. BattleplanCard.mapData) re-run and see the updated maps.
const registryVersion = ref(0);
const history = ref<Snapshot[]>([]);
const rulesDocuments = ref<RuleDocument[]>([]);
const battleTactics = ref<IBattleTacticCard[]>([]);
const armies = ref<AuthoredArmy[]>([]);
const regimentsOfRenown = ref<IRegimentOfRenown[]>([]);

// Deep-clone plain JSON-serializable objects. Always uses JSON round-trip to
// avoid issues with Vue reactive proxies that structuredClone cannot handle.
function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function pushHistory() {
  const snapshot: Snapshot = {
    battleplans: deepClone(battleplans.value as Record<string, BattleplanEntry>),
    currentPlan: currentPlan.value,
    rulesDocuments: deepClone(rulesDocuments.value),
    battleTactics: deepClone(battleTactics.value),
    armies: deepClone(armies.value),
    regimentsOfRenown: deepClone(regimentsOfRenown.value),
  };
  history.value.push(snapshot);
  if (history.value.length > HISTORY_LIMIT) {
    history.value.shift();
  }
}

function emptyMap(): IBattleplanMap {
  return { objectives: [], terrain: [], zones: [] };
}

// True when no authored content has been specified — used to keep a plan map-only
// rather than emitting a blank authored block (just a name) into the bundle. The
// name is never part of "content" (it's the entry key); see shared hasAuthoredContent.
function isAuthoredEmpty(fields: AuthoredFields): boolean {
  return !hasAuthoredContent(fields);
}

const TWIST_PREFIX = '**TWIST**:';

function formatTwistForStorage(twist: string | undefined): string | undefined {
  const trimmed = twist?.trim();
  if (!trimmed) return undefined;
  if (/^\*\*twist\*\*:/i.test(trimmed)) return trimmed;
  return `${TWIST_PREFIX} ${trimmed}`;
}

// The authored battleplan's name is redundant with its entry key, so it isn't
// written to the exported bundle. Strip it from each authored block; the shared
// consumers and loadBundle re-derive the name from the key.
function stripAuthoredNames(
  plans: Record<string, BattleplanEntry>,
): Record<string, BattleplanEntry> {
  const out: Record<string, BattleplanEntry> = {};
  for (const [name, entry] of Object.entries(plans)) {
    if (entry.authored) {
      const { name: _omitName, ...rest } = entry.authored;
      out[name] = { ...entry, authored: rest as IBattleplan };
    } else {
      out[name] = entry;
    }
  }
  return out;
}

// Derive a name-keyed authored record for consumers that only care about the
// authored rules half of each entry (preview pane, authoring form).
function authoredRecord(): Record<string, IBattleplan> {
  const out: Record<string, IBattleplan> = {};
  for (const [name, entry] of Object.entries(battleplans.value)) {
    if (entry.authored) out[name] = entry.authored;
  }
  return out;
}

function syncRegistry() {
  if (battlePack.value) {
    registerBattlepackBundle({ battlePack: battlePack.value, battleplans: battleplans.value });
  }
  registerArmyBundle(armies.value, battlePack.value ?? 'studio');
  registryVersion.value++;
}

export function useWorkspace() {
  return {
    battlePack: computed(() => battlePack.value),
    planNames: computed(() => Object.keys(battleplans.value)),
    currentPlan: computed(() => currentPlan.value),
    currentMap: computed(() =>
      currentPlan.value ? (battleplans.value[currentPlan.value]?.map ?? null) : null,
    ),
    battleplans: computed(() => battleplans.value),
    authored: computed(() => authoredRecord()),
    registryVersion: computed(() => registryVersion.value),
    canUndo: computed(() => history.value.length > 0),

    rulesDocuments: computed(() => rulesDocuments.value),
    battleTactics: computed(() => battleTactics.value),
    armies: computed(() => armies.value),
    regimentsOfRenown: computed(() => regimentsOfRenown.value),

    reset() {
      battlePack.value = null;
      battleplans.value = {};
      currentPlan.value = null;
      rulesDocuments.value = [];
      battleTactics.value = [];
      armies.value = [];
      regimentsOfRenown.value = [];
      history.value = [];
    },
    newPack(pack: BattlePack) {
      pushHistory();
      battlePack.value = pack;
      battleplans.value = {};
      currentPlan.value = null;
      rulesDocuments.value = [];
      battleTactics.value = [];
      armies.value = [];
      regimentsOfRenown.value = [];
      syncRegistry();
    },
    setRulesDocuments(docs: RuleDocument[]) {
      pushHistory();
      rulesDocuments.value = docs;
      syncRegistry();
    },
    setBattleTactics(cards: IBattleTacticCard[]) {
      pushHistory();
      battleTactics.value = cards;
      syncRegistry();
    },
    setRegimentsOfRenown(regiments: IRegimentOfRenown[]) {
      pushHistory();
      regimentsOfRenown.value = regiments;
      syncRegistry();
    },
    addPlan(name: string) {
      pushHistory();
      if (!battleplans.value[name]) battleplans.value[name] = { map: emptyMap() };
      currentPlan.value = name;
      syncRegistry();
    },
    selectPlan(name: string) {
      currentPlan.value = name;
    },
    // Duplicate a plan (map + authored), inserting the copy right after the source
    // and selecting it. Returns the new name (a unique "<name> copy[ N]").
    duplicatePlan(name: string): string | null {
      const source = battleplans.value[name];
      if (!source) return null;
      pushHistory();
      const base = `${name} copy`;
      let copyName = base;
      for (let n = 2; battleplans.value[copyName] !== undefined; n++) copyName = `${base} ${n}`;
      const clone = deepClone(source);
      if (clone.authored) clone.authored.name = copyName;
      const next: Record<string, BattleplanEntry> = {};
      for (const [k, v] of Object.entries(battleplans.value)) {
        next[k] = v;
        if (k === name) next[copyName] = clone;
      }
      battleplans.value = next;
      currentPlan.value = copyName;
      syncRegistry();
      return copyName;
    },
    deletePlan(name: string) {
      if (battleplans.value[name] === undefined) return;
      pushHistory();
      const next = { ...battleplans.value };
      delete next[name];
      battleplans.value = next;
      if (currentPlan.value === name) {
        currentPlan.value = Object.keys(battleplans.value)[0] ?? null;
      }
      syncRegistry();
    },
    renamePlan(oldName: string, newName: string): boolean {
      const trimmed = newName.trim();
      if (!trimmed || trimmed === oldName) return false;
      if (battleplans.value[oldName] === undefined) return false;
      if (battleplans.value[trimmed] !== undefined) return false; // name already taken
      pushHistory();
      // Rebuild the record to preserve key order, swapping the renamed key in
      // place and keeping the authored battleplan's own name field in sync.
      const renamed: Record<string, BattleplanEntry> = {};
      for (const [k, v] of Object.entries(battleplans.value)) {
        if (k === oldName) {
          renamed[trimmed] = v.authored ? { ...v, authored: { ...v.authored, name: trimmed } } : v;
        } else {
          renamed[k] = v;
        }
      }
      battleplans.value = renamed;
      if (currentPlan.value === oldName) currentPlan.value = trimmed;
      syncRegistry();
      return true;
    },
    movePlan(fromIndex: number, toIndex: number) {
      const names = Object.keys(battleplans.value);
      if (
        fromIndex < 0 ||
        fromIndex >= names.length ||
        toIndex < 0 ||
        toIndex >= names.length ||
        fromIndex === toIndex
      ) {
        return;
      }
      pushHistory();
      const order = [...names];
      const [moved] = order.splice(fromIndex, 1);
      order.splice(toIndex, 0, moved);
      // Rebuild in the new key order (insertion order drives the plan list and
      // the exported bundle order).
      const reordered: Record<string, BattleplanEntry> = {};
      for (const name of order) reordered[name] = battleplans.value[name];
      battleplans.value = reordered;
      syncRegistry();
    },
    updateCurrentMap(map: IBattleplanMap) {
      pushHistory();
      if (currentPlan.value) {
        battleplans.value[currentPlan.value] = {
          ...battleplans.value[currentPlan.value],
          map,
        };
      }
      syncRegistry();
    },
    setPlanFields(name: string, fields: AuthoredFields) {
      pushHistory();
      const entry = { ...battleplans.value[name] };
      if (isAuthoredEmpty(fields)) {
        // Nothing authored — drop any authored content so the plan stays map-only
        // and no empty authored component is emitted into the bundle.
        delete entry.authored;
      } else {
        entry.authored = {
          name,
          rulesText: fields.rulesText ?? '',
          components: [],
          twist: formatTwistForStorage(fields.twist),
          scoringText: fields.scoringText,
          scoringRules: fields.scoringRules ?? [],
          abilities: fields.abilities ?? [],
          sections: fields.sections?.length ? fields.sections : undefined,
        };
      }
      battleplans.value[name] = entry;
    },
    undo() {
      if (history.value.length === 0) return;
      const snapshot = history.value.pop()!;
      battleplans.value = deepClone(snapshot.battleplans);
      currentPlan.value = snapshot.currentPlan;
      rulesDocuments.value = deepClone(snapshot.rulesDocuments);
      battleTactics.value = deepClone(snapshot.battleTactics);
      armies.value = deepClone(snapshot.armies);
      regimentsOfRenown.value = deepClone(snapshot.regimentsOfRenown);
      syncRegistry();
    },
    loadBundle(bundle: BattlepackBundle) {
      battlePack.value = bundle.battlePack;
      // Re-derive each authored block's name from its key. Saved bundles omit the
      // redundant name (see stripAuthoredNames); the in-memory model keeps it so
      // the preview card can resolve its map by name.
      const plans = deepClone(bundle.battleplans);
      for (const [name, entry] of Object.entries(plans)) {
        if (entry.authored) entry.authored.name = name;
      }
      battleplans.value = plans;
      currentPlan.value = Object.keys(battleplans.value)[0] ?? null;
      rulesDocuments.value = deepClone(bundle.rulesDocuments ?? []);
      battleTactics.value = deepClone(bundle.battleTactics ?? []);
      // Strip any baked-in derived keywords so the stored set is author-only;
      // the editor re-derives the locked ones for display. No-op on the clean
      // localStorage copy; removes them from imported/exported files.
      armies.value = deepClone(bundle.armies ?? []).map(stripDerivedUnitKeywords);
      regimentsOfRenown.value = deepClone(bundle.regimentsOfRenown ?? []).map(
        stripDerivedRegimentUnitKeywords,
      );
      history.value = [];
      syncRegistry();
    },
    snapshotBundle(): BattlepackBundle {
      return {
        battlePack: battlePack.value as BattlePack,
        battleplans: deepClone(battleplans.value),
        ...(rulesDocuments.value.length ? { rulesDocuments: rulesDocuments.value } : {}),
        ...(battleTactics.value.length ? { battleTactics: battleTactics.value } : {}),
        ...(armies.value.length ? { armies: deepClone(armies.value) } : {}),
        ...(regimentsOfRenown.value.length
          ? { regimentsOfRenown: deepClone(regimentsOfRenown.value) }
          : {}),
      };
    },
    exportBundle(): string {
      const out: BattlepackBundle = {
        studioVersion: STUDIO_VERSION,
        battlePack: battlePack.value!,
        battleplans: stripAuthoredNames(battleplans.value),
        ...(rulesDocuments.value.length ? { rulesDocuments: rulesDocuments.value } : {}),
        ...(battleTactics.value.length ? { battleTactics: battleTactics.value } : {}),
        ...(regimentsOfRenown.value.length
          ? { regimentsOfRenown: regimentsOfRenown.value.map(withDerivedRegimentUnitKeywords) }
          : {}),
        // Bake derived keywords (army/GA/type/flags) into each unit's keywords so
        // consumers see the full keyword line; the studio strips them on load.
        ...(armies.value.length ? { armies: armies.value.map(withDerivedUnitKeywords) } : {}),
      };
      return JSON.stringify(out, null, 2);
    },
    addArmy(name: string) {
      pushHistory();
      armies.value = [...armies.value, makeAuthoredArmy(name)];
      syncRegistry();
    },
    updateArmy(index: number, army: AuthoredArmy) {
      if (index < 0 || index >= armies.value.length) return;
      pushHistory();
      armies.value = armies.value.map((a, i) => (i === index ? army : a));
      syncRegistry();
    },
    deleteArmy(index: number) {
      if (index < 0 || index >= armies.value.length) return;
      pushHistory();
      armies.value = armies.value.filter((_, i) => i !== index);
      syncRegistry();
    },
    duplicateArmy(index: number): number | null {
      const src = armies.value[index];
      if (!src) return null;
      pushHistory();
      const base = `${src.name} copy`;
      const names = new Set(armies.value.map((a) => a.name));
      let name = base;
      for (let n = 2; names.has(name); n++) name = `${base} ${n}`;
      const copy = { ...deepClone(src), name };
      const at = index + 1;
      armies.value = [...armies.value.slice(0, at), copy, ...armies.value.slice(at)];
      syncRegistry();
      return at;
    },
    moveArmy(from: number, to: number) {
      if (
        from < 0 ||
        from >= armies.value.length ||
        to < 0 ||
        to >= armies.value.length ||
        from === to
      )
        return;
      pushHistory();
      const next = [...armies.value];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      armies.value = next;
      syncRegistry();
    },
  };
}
